import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./ClassWiseRecoveryBar.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

/* Read a CSS custom-property from :root */
const cssVar = (name) => {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
};

/* ── Regular (non-stacked) colour palette ── */
const COLOR_PALETTE = [
  { darkVar: "--violet-dark", lightVar: "--violet-light" },
  { darkVar: "--green-dark", lightVar: "--green-light" },
  { darkVar: "--pink-dark", lightVar: "--pink-light" },
  { darkVar: "--orange-dark", lightVar: "--orange-light" },
  { darkVar: "--lime-dark", lightVar: "--lime-light" },
  { darkVar: "--red-dark", lightVar: "--red-light" },
];

/* ── Strong / Weak stacked segments ── */
const DEFAULT_SEGMENTS = [
  { key: "strong", darkVar: "--green-dark", lightVar: "--green-light" },
  { key: "weak", darkVar: "--red-dark", lightVar: "--red-light" },
];

const getColorPair = (index) => {
  const len = COLOR_PALETTE.length;
  const numeric = Number.isFinite(index) ? index : 0;
  const safeIdx = ((numeric % len) + len) % len;
  const p = COLOR_PALETTE[safeIdx] || COLOR_PALETTE[0];
  return { dark: cssVar(p.darkVar), light: cssVar(p.lightVar) };
};

/* Full-chart-area gradient for regular bars */
const buildGradient = (canvasCtx, chartArea, idx) => {
  const { dark, light } = getColorPair(idx);
  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  g.addColorStop(0, dark);
  g.addColorStop(1, light);
  return g;
};

/* Per-bar gradient for stacked segments */
const buildPerBarGradient = (chart, ctx, colorCfg) => {
  const { ctx: canvasCtx, chartArea } = chart;
  const dark = cssVar(colorCfg.darkVar);
  const light = cssVar(colorCfg.lightVar);

  if (!chartArea) return dark;

  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  g.addColorStop(0, dark);
  g.addColorStop(1, light);
  return g;
};

/* Draws value labels centred inside each stacked segment */
const makeValueLabelPlugin = (suffix = "%") => ({
  id: "stackedValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx: c } = chart;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((bar, bi) => {
        const val = ds.data[bi];
        if (!val) return; // skip empty/zero segments
        const segH = Math.abs(bar.base - bar.y);
        if (segH < 14) return; // skip segments too short to label
        c.save();
        c.fillStyle = "#1f2937";
        c.font = "600 12px -apple-system,BlinkMacSystemFont,sans-serif";
        c.textAlign = "center";
        c.textBaseline = "middle";
        c.fillText(`${val}${suffix}`, bar.x, (bar.y + bar.base) / 2);
        c.restore();
      });
    });
  },
});

const ClassWiseRecoveryBar = ({
  icon = null,
  title = "Performance Overview",
  chartTitle = "",
  datasetLabel = "Dataset 1",
  valueLabel = "Exam Percentage",
  data = [],
  loading = false,
  error = null,
  maxY = 100,
  stepSize = 10,
  valueSuffix = "%",
  yAxisLabel = "",
  tooltipAfterBody = null,
  onBarClick,
  bottomHint = false,
  stacked = false,
  drilldown = false,
  serverDrilldown = false,
  serverDrillLevels = ["class", "section"],
  level = "class",
  onBack,
  segments = DEFAULT_SEGMENTS,
  segmentLabels = { strong: "Strong", weak: "Weak" },
  sectionWiseSuffix = " - Section Wise",
  subjectWiseSuffix = " - Subject Wise",
  classHint = "Click any class to view section-wise breakdown",
  sectionHint = "Click any section to view subject-wise breakdown",
  subjectHint = "",
  backHint = "click ✕ to go back",
  info = "",
  onViewAll,
  viewAllLabel = "View All",
}) => {
  /* non-stacked: exam → subject drill-down */
  const [selectedExam, setSelectedExam] = useState(null);
  /* stacked (INTERNAL): class → section → subject drill-down */
  const [drillLevel, setDrillLevel] = useState("class");
  const [classIdx, setClassIdx] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(null);

  /* Value label plugin re-built only when the suffix changes */
  const valueLabelPlugin = useMemo(
    () => makeValueLabelPlugin(valueSuffix),
    [valueSuffix],
  );

  /* Active level: parent-controlled in server mode, internal otherwise */
  const lvl = serverDrilldown ? level : drillLevel;

  let chartData;
  let chartOptions;
  let extraPlugins = [];
  let headerTitle = title;
  let hint = "";
  let showClose = false;

  if (stacked) {
    /* ── Resolve rows for current drill level ── */
    let rows = data;
    if (
      !serverDrilldown &&
      drilldown &&
      drillLevel === "section" &&
      classIdx != null
    ) {
      rows = data[classIdx]?.sections || [];
    } else if (
      !serverDrilldown &&
      drilldown &&
      drillLevel === "subject" &&
      classIdx != null &&
      sectionIdx != null
    ) {
      rows = data[classIdx]?.sections?.[sectionIdx]?.subjects || [];
    }

    const segDefs = segments.map((s) => ({
      ...s,
      label: segmentLabels[s.key] ?? s.key,
    }));

    chartData = {
      labels: rows.map((r) => r.label),
      datasets: segDefs.map((seg, si) => ({
        label: seg.label,
        key: seg.key,
        data: rows.map((r) => r[seg.key] ?? 0),
        backgroundColor: (ctx) => buildPerBarGradient(ctx.chart, ctx, seg),
        hoverBackgroundColor: (ctx) => buildPerBarGradient(ctx.chart, ctx, seg),
        borderWidth: 0,
        borderRadius:
          si === segDefs.length - 1
            ? { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
            : 0,
        borderSkipped: false,
        barPercentage: 0.55,
        categoryPercentage: 0.7,
      })),
    };

    const canDrill = serverDrilldown
      ? serverDrillLevels.includes(lvl)
      : drilldown && (drillLevel === "class" || drillLevel === "section");

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      /* Focus on individual segment hover matching Image 1 */
      interaction: { mode: "nearest", intersect: true },
      onClick: (_evt, elements) => {
        if (!elements?.length) return;
        const idx = elements[0].index;

        if (serverDrilldown) {
          if (serverDrillLevels.includes(lvl)) {
            if (typeof onBarClick === "function")
              onBarClick(rows[idx], idx, lvl);
          }
          return;
        }

        if (!drilldown) return;
        if (drillLevel === "class") {
          const cls = data[idx];
          if (cls?.sections?.length) {
            setClassIdx(idx);
            setSectionIdx(null);
            setDrillLevel("section");
            if (typeof onBarClick === "function") onBarClick("section", cls);
          }
        } else if (drillLevel === "section") {
          const secs = data[classIdx]?.sections || [];
          if (secs[idx]?.subjects?.length) {
            setSectionIdx(idx);
            setDrillLevel("subject");
            if (typeof onBarClick === "function")
              onBarClick("subject", secs[idx]);
          }
        }
      },
      onHover: (evt, elements) => {
        const canvas = evt?.native?.target;
        if (canvas) {
          canvas.style.cursor =
            canDrill && elements.length ? "pointer" : "default";
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: "#374151", font: { size: 11, weight: "500" } },
          border: { display: false },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          max: maxY,
          ticks: {
            stepSize,
            color: "#6B7280",
            callback: (value) => `${value}${valueSuffix}`,
          },
          grid: { color: "#E5E7EB" },
          border: { display: false },
          title: {
            display: !!yAxisLabel,
            text: yAxisLabel,
            color: "#6b7280",
            font: { size: 13, weight: "600" },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            pointStyle: "rect",
            boxWidth: 14,
            boxHeight: 10,
            padding: 16,
            font: { size: 12, weight: "500" },
            color: "#4b5563",
            filter: (legendItem) => legendItem.text?.trim() !== "",
          },
        },
        tooltip: {
          mode: "nearest",
          intersect: true,
          backgroundColor: "#000000",
          padding: 10,
          cornerRadius: 6,
          displayColors: false, // Hide default color box to match Image 1
          callbacks: {
            title: () => "", // Hide category title header inside tooltip
            label: (ctx) => {
              const dataIdx = ctx.dataIndex;
              const row = rows[dataIdx];
              if (!row) return "";

              const key = ctx.dataset.key; // "paid", "partly", or "notPaid"

              if (key === "paid") {
                return [
                  `Students Paid : ${row.paidStudents}`,
                  `Amount Collected : ${row.paidAmountDisplay}`,
                ];
              } else if (key === "partly") {
                return [
                  `Students Partly paid : ${row.partlyStudents}`,
                  `Pending Amount : ${row.partlyAmountDisplay}`,
                ];
              } else if (key === "notPaid") {
                return [
                  `Students Not paid : ${row.notPaidStudents}`,
                  `Pending Amount : ${row.notPaidAmountDisplay}`,
                ];
              }
              return "";
            },
          },
        },
      },
    };

    extraPlugins = [valueLabelPlugin];

    if (serverDrilldown || drilldown) {
      if (lvl === "section") {
        headerTitle = `${title}${sectionWiseSuffix}`;
        showClose = true;
      } else if (lvl === "subject") {
        headerTitle = `${title}${subjectWiseSuffix}`;
        showClose = true;
      }
    }

    if (bottomHint) {
      const withBack = (base) =>
        base && backHint ? `${base} - ${backHint}` : base || backHint || "";
      if (serverDrilldown || drilldown) {
        if (lvl === "class") {
          hint = classHint;
        } else if (lvl === "section") {
          hint = withBack(sectionHint);
        } else if (lvl === "subject") {
          hint = withBack(subjectHint);
        }
      } else {
        hint = classHint;
      }
    }
  } else {
    /* ───────────── Non-stacked bar ───────────── */
    const isDrillDown = selectedExam !== null;
    const rawSelectedIndex = isDrillDown
      ? data.findIndex((d) => d.exam_name === selectedExam)
      : -1;
    const selectedExamIndex =
      isDrillDown && rawSelectedIndex < 0 ? 0 : rawSelectedIndex;

    let labels = [];
    let values = [];

    if (isDrillDown) {
      const exam = data.find((d) => d.exam_name === selectedExam);
      labels = exam?.subjects?.map((s) => s.name) || [];
      values = exam?.subjects?.map((s) => s.mark) || [];
    } else {
      labels = data.map((d) => d.exam_name);
      values = data.map((d) => d.overAllMark);
    }

    chartData = {
      labels,
      datasets: [
        {
          label: datasetLabel,
          data: values,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) return cssVar(getColorPair(0).dark);
            const colorIndex = isDrillDown ? selectedExamIndex : ctx.dataIndex;
            return buildGradient(canvasCtx, chartArea, colorIndex);
          },
          borderColor: (ctx) => {
            const colorIndex = isDrillDown ? selectedExamIndex : ctx.dataIndex;
            return getColorPair(colorIndex).dark;
          },
          borderWidth: 1,
          borderRadius: 8,
          barPercentage: 0.55,
          categoryPercentage: 0.7,
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: !!chartTitle, text: chartTitle },
        tooltip: {
          backgroundColor: "#1F2937",
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: "bold" },
          callbacks: {
            label: (ctx) => `${valueLabel}: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: maxY,
          ticks: { stepSize, color: "#6B7280" },
          grid: { color: "#E5E7EB" },
        },
        x: {
          ticks: { color: "#374151", font: { weight: "500" } },
          grid: { display: false },
        },
      },
      onClick: (_event, elements) => {
        if (isDrillDown) return;
        if (elements?.length > 0) {
          const idx = elements[0].index;
          const clicked = data[idx];
          if (clicked?.subjects?.length > 0) {
            setSelectedExam(clicked.exam_name);
            if (typeof onBarClick === "function") onBarClick(clicked.exam_name);
          }
        }
      },
    };

    headerTitle = isDrillDown
      ? `${title} - ${selectedExam} (Subject-wise)`
      : title;
    showClose = isDrillDown;
    if (bottomHint) {
      hint = isDrillDown ? "Subject-wise breakdown - click ✕ to go back" : "  ";
    }
  }

  const handleClose = () => {
    if (serverDrilldown) {
      if (typeof onBack === "function") onBack();
      return;
    }
    if (stacked) {
      if (drillLevel === "subject") {
        setDrillLevel("section");
        setSectionIdx(null);
      } else if (drillLevel === "section") {
        setDrillLevel("class");
        setClassIdx(null);
      }
    } else {
      setSelectedExam(null);
    }
  };

  const renderOverlay = () => {
    if (loading) {
      return (
        <div className={styles.overlay}>
          <span className={styles.overlayText}>
            <span className={styles.spinner} />
            Loading…
          </span>
        </div>
      );
    }
    if (error) {
      return (
        <div className={styles.overlay}>
          <span className={styles.overlayText}>{error}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-100">
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {icon && (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          )}
          <h3 className="chartTitle">{headerTitle}</h3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {info && (
            <div className={styles.infoNote}>
              {" "}
              <strong>Note:</strong> {info}
            </div>
          )}
          {stacked && onViewAll && lvl === "class" && (
            <button
              type="button"
              onClick={onViewAll}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                color: cssVar("--orange-dark"),
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {viewAllLabel}
            </button>
          )}
          {showClose && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close drill-down view"
              className={styles.closeBtn}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Chart ── */}
      <div className={styles.chartContainer} style={{ position: "relative" }}>
        {renderOverlay()}
        <Bar data={chartData} options={chartOptions} plugins={extraPlugins} />
      </div>

      {/* ── Hint ── */}
      {hint && <div className={styles.viewHint}>{hint}</div>}
    </div>
  );
};

export default ClassWiseRecoveryBar;